import type { InspectionType, PhotoSection, Role } from '@room-verify/shared';

/**
 * The only place this app talks to the network.
 *
 * Every failure resolves to an `ApiError` carrying a stable code, so screens can show
 * a specific message in the active language instead of a generic "request failed".
 */

export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly context: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }

  /** Worth another attempt without the user changing anything. */
  get retryable(): boolean {
    return this.code === 'NETWORK' || this.status === 0 || this.status >= 500;
  }

  get isAuthExpiry(): boolean {
    return this.code === 'SESSION_EXPIRED' || this.code === 'UNAUTHORIZED';
  }
}

const TOKEN_KEY = 'roomverify.token';

export const loadToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const saveToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage may be unavailable; the in-memory token still works this session */
  }
};

async function parseError(response: Response): Promise<ApiError> {
  let code = 'INTERNAL_ERROR';
  let context: Record<string, unknown> = {};
  try {
    const payload = (await response.json()) as { error?: string } & Record<string, unknown>;
    if (typeof payload.error === 'string') code = payload.error;
    const { error: _ignored, ...rest } = payload;
    context = rest;
  } catch {
    /* a non-JSON error body still deserves a typed failure */
  }
  return new ApiError(code, response.status, context);
}

interface RequestOptions {
  method?: string;
  token?: string | null;
  json?: unknown;
  body?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  if (options.token) headers.set('authorization', `Bearer ${options.token}`);

  let body = options.body;
  if (options.json !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(options.json);
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
    });
  } catch (cause) {
    // fetch only rejects for transport-level problems: offline, DNS, TLS, abort.
    throw new ApiError('NETWORK', 0, { cause: String(cause) });
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ types */

export interface SessionUser {
  id: string;
  fullName: string;
  role: Role;
}

export interface LoginResult {
  token: string;
  expiresAt: string;
  user: SessionUser;
}

export interface ConfirmedPhoto {
  id: string;
  section: PhotoSection;
  sequence: number;
  capturedAt: string;
  uploadedAt: string;
  hash: string;
  byteSize: number;
  alreadyConfirmed?: boolean;
}

export interface Inspection {
  id: string;
  roomNumber: string;
  inspectionType: InspectionType;
  status: 'IN_PROGRESS' | 'COMPLETED';
  startedAt: string;
  supervisorName: string;
  requirements: Record<PhotoSection, number>;
  photos: ConfirmedPhoto[];
}

export interface ActiveInspection {
  id: string;
  roomNumber: string;
  inspectionType: InspectionType;
  startedAt: string;
  confirmedSlots: { section: PhotoSection; sequence: number }[];
}

export interface MyWorkItem {
  id: string;
  roomNumber: string;
  inspectionType: InspectionType;
  completedAt: string;
  durationSeconds: number;
  photoCount: number;
  itemsComplete: boolean;
  missingItems: string[];
  otherMissingItem: string | null;
}

export interface MyWork {
  date: string;
  stats: { total: number; checkout: number; clean: number };
  results: MyWorkItem[];
}

export interface CompletionResult {
  id: string;
  roomNumber: string;
  inspectionType: InspectionType;
  status: 'COMPLETED';
  completedAt: string;
  durationSeconds: number;
  businessDate: string;
  itemsComplete: boolean;
  missingItems: string[];
  otherMissingItem: string | null;
}

/* -------------------------------------------------------------- endpoints */

export const api = {
  health: () => request<{ ok: boolean; buildId: string }>('/health'),

  login: (username: string, password: string) =>
    request<LoginResult>('/auth/login', { method: 'POST', json: { username, password } }),

  me: (token: string) => request<{ user: SessionUser; expiresAt: string }>('/auth/me', { token }),

  logout: (token: string) => request<{ ok: true }>('/auth/logout', { method: 'POST', token }),

  startInspection: (token: string, roomNumber: string, inspectionType: InspectionType) =>
    request<{ inspection: Inspection }>('/inspections', {
      method: 'POST',
      token,
      json: { roomNumber, inspectionType },
    }),

  getInspection: (token: string, id: string) =>
    request<{ inspection: Inspection }>(`/inspections/${id}`, { token }),

  activeInspection: (token: string) =>
    request<{ inspection: ActiveInspection | null }>('/supervisor/active', { token }),

  myWork: (token: string, date?: string) =>
    request<MyWork>(`/supervisor/my-work${date ? `?date=${date}` : ''}`, { token }),

  completeInspection: (
    token: string,
    id: string,
    items: { complete: boolean; missingItems: string[]; otherMissingItem: string | null },
  ) =>
    request<{ inspection: CompletionResult }>(`/inspections/${id}/complete`, {
      method: 'POST',
      token,
      json: items,
    }),

  /** Single upload attempt. Retry policy lives in `uploadPhotoWithRetry`. */
  uploadPhoto: (
    token: string,
    inspectionId: string,
    slot: { section: PhotoSection; sequence: number; capturedAt: string },
    blob: Blob,
    signal?: AbortSignal,
  ) =>
    request<{ photo: ConfirmedPhoto }>(`/inspections/${inspectionId}/photos`, {
      method: 'POST',
      token,
      body: blob,
      signal,
      headers: {
        'content-type': blob.type || 'image/jpeg',
        'x-section': slot.section,
        'x-sequence': String(slot.sequence),
        'x-captured-at': slot.capturedAt,
      },
    }),
};

const RETRY_DELAYS_MS = [500, 1200, 2500];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type UploadPhase = 'uploading' | 'retrying' | 'uploaded';

/**
 * Uploads one photo, retrying transport failures and 5xx.
 *
 * Safe to retry because the endpoint is idempotent on (inspection, section, sequence):
 * an attempt that actually succeeded before the connection dropped comes back as
 * `alreadyConfirmed` rather than as a duplicate or an error.
 *
 * A non-retryable failure (415, 413, 409, 401) throws immediately — retrying those
 * would only waste the supervisor's time on a mobile connection.
 */
export async function uploadPhotoWithRetry(
  token: string,
  inspectionId: string,
  slot: { section: PhotoSection; sequence: number; capturedAt: string },
  blob: Blob,
  onPhase: (phase: UploadPhase, attempt: number) => void,
): Promise<ConfirmedPhoto> {
  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    onPhase(attempt === 0 ? 'uploading' : 'retrying', attempt);
    try {
      const result = await api.uploadPhoto(token, inspectionId, slot, blob);
      onPhase('uploaded', attempt);
      return result.photo;
    } catch (error) {
      const apiError = error instanceof ApiError ? error : new ApiError('INTERNAL_ERROR', 0);
      if (!apiError.retryable) throw apiError;
      lastError = apiError;
      const delay = RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) await sleep(delay);
    }
  }

  throw lastError ?? new ApiError('NETWORK', 0);
}
